// components/dialog/SiteSettingsDialog.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Grid,
  Divider,
  Chip,
} from "@mui/material";
import { Settings, Security, Domain, Update } from "@mui/icons-material";
import { getResponsiveDialogProps } from "../../theme/responsive";
import Interceptor from "../../services/Interceptor";

const SiteSettingsDialog = ({ open, onClose, site, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: "",
    status: "",
    customDomain: "",
    sslEnabled: false,
    wpVersion: "",
    environment: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [latestWpVersion, setLatestWpVersion] = useState(null);

  useEffect(() => {
    if (open) {
      Interceptor.get("/sites/wp-versions")
        .then((res) => {
          if (res.data?.data?.latestVersion) {
            setLatestWpVersion(res.data.data.latestVersion);
          }
        })
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (site) {
      setFormData({
        name: site.name || "",
        status: site.status || "",
        customDomain: site.customDomain || "",
        sslEnabled: site.sslEnabled || false,
        wpVersion: site.wpVersion || "",
        environment: site.environment || "",
      });
    }
  }, [site]);

  const handleSubmit = async () => {
    if (!site) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Separate domain changes from other settings
      const otherChanges = {};
      Object.keys(formData).forEach((key) => {
        if (key === "customDomain") return; // handled separately
        if (
          formData[key] !== "" &&
          formData[key] !== null &&
          formData[key] !== undefined &&
          formData[key] !== site[key]
        ) {
          otherChanges[key] = formData[key];
        }
      });

      if (Object.keys(otherChanges).length === 0) {
        setError("No changes detected");
        setLoading(false);
        return;
      }

      await onUpdate(site.id, otherChanges);
      setSuccess("Site settings updated successfully");
      setTimeout(() => {
        onClose();
        setSuccess("");
      }, 1500);
      setLoading(false);
    } catch (err) {
      setError(err.message || "Failed to update site settings");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "SUSPENDED":
        return "error";
      case "MAINTENANCE":
        return "warning";
      case "PENDING":
        return "info";
      default:
        return "default";
    }
  };

  const getEnvironmentColor = (env) => {
    switch (env) {
      case "PRODUCTION":
        return "error";
      case "STAGING":
        return "warning";
      case "SANDBOX":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        {...getResponsiveDialogProps("large")}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Settings />
            <Typography variant="h6">Site Settings - {site?.name}</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Site Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    margin="normal"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl
                    fullWidth
                    margin="normal"
                    sx={{ flex: "1 1 150px", minWidth: 100 }}
                  >
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="SUSPENDED">Suspended</MenuItem>
                      <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl
                    fullWidth
                    margin="normal"
                    sx={{ flex: "1 1 150px", minWidth: 100 }}
                  >
                    <InputLabel>Environment</InputLabel>
                    <Select
                      value={formData.environment}
                      label="Environment"
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          environment: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="SANDBOX">Sandbox</MenuItem>
                      <MenuItem value="STAGING">Staging</MenuItem>
                      <MenuItem value="PRODUCTION">Production</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="WordPress Version"
                    value={
                      site?.versionInfo?.wpVersion || site?.wpVersion || "N/A"
                    }
                    InputProps={{
                      readOnly: true,
                      endAdornment: latestWpVersion &&
                        site?.versionInfo?.wpVersion &&
                        site.versionInfo.wpVersion !== latestWpVersion && (
                          <Chip
                            label={`${latestWpVersion} available`}
                            size="small"
                            color="warning"
                            icon={<Update fontSize="small" />}
                          />
                        ),
                    }}
                    helperText={
                      latestWpVersion &&
                      site?.versionInfo?.wpVersion === latestWpVersion
                        ? "Up to date"
                        : latestWpVersion
                          ? `Latest: ${latestWpVersion}`
                          : undefined
                    }
                    sx={{ flex: "1 1 150px", minWidth: 200 }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>

            {/* Domain & SSL Summary */}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Domain fontSize="small" />
                Domain & SSL
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Platform Domain:{" "}
                  <Typography
                    component="span"
                    fontFamily="monospace"
                    variant="body2"
                  >
                    {site?.domain}
                  </Typography>
                </Typography>
                {site?.customDomain && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Custom Domain:{" "}
                      <Typography
                        component="span"
                        fontFamily="monospace"
                        variant="body2"
                      >
                        {site.customDomain}
                      </Typography>
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        site.metadata?.domainVerified
                          ? "Verified"
                          : "Pending DNS"
                      }
                      color={
                        site.metadata?.domainVerified ? "success" : "warning"
                      }
                    />
                  </Box>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  SSL: {site?.sslEnabled ? "Enabled" : "Disabled"}
                </Typography>
              </Box>

              <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Manage custom domains, DNS setup, and SSL in the{" "}
                  <strong>Settings tab</strong>.
                </Typography>
              </Alert>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>

            {/* Current Status */}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Security fontSize="small" />
                Current Status
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip
                  label={`Status: ${site?.status}`}
                  color={getStatusColor(site?.status)}
                  variant="outlined"
                />
                <Chip
                  label={`Environment: ${site?.environment}`}
                  color={getEnvironmentColor(site?.environment)}
                  variant="outlined"
                />
                <Chip
                  label={`SSL: ${site?.sslEnabled ? "Enabled" : "Disabled"}`}
                  color={site?.sslEnabled ? "success" : "default"}
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={<Update />}
          >
            {loading ? "Updating..." : "Update Settings"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SiteSettingsDialog;
